<template>
  <div class="flex flex-column flex-grow-1">
    <div
      v-if="chartingData"
      class="flex align-items-center mb-4 justify-content-around"
    >
      <div class="flex flex-column">
        <span class="flex-shrink-0 mr-3">Operating Range: </span>
        <PumpFrequencyRange
          v-model.lazy="frequencyRange"
          class="mr-2"
          :motor-poles="[chartingData.context.motorPoles]"
          @update:model-value="frequencyRangeUpdated"
        />
      </div>
      <div class="flex flex-column justify-content-end">
        <div v-if="usesCorrectedViscosityRate" class="flex text-xs p-1">
          <Badge severity="danger">Uses corrected viscosity rate</Badge>
        </div>
      </div>
    </div>
    <div
      v-if="!chartingData && tornadoChart?.error.value"
      class="flex align-items-center justify-content-center p-4 text-sm"
    >
      Unable to calculate the tornado charts: {{ tornadoChart.error.value }}
    </div>
    <div
      v-else-if="!chartingData && tornadoChart"
      class="flex flex-column align-items-center justify-content-center p-4 gap-2"
    >
      <ProgressSpinner style="width: 2.5rem; height: 2.5rem" />
      <span class="text-sm">Calculating tornado charts…</span>
    </div>
    <ProgressBar
      v-if="chartingData && tornadoChart?.pending.value"
      mode="indeterminate"
      style="height: 0.25rem"
    />
    <div
      v-if="chartingData || !tornadoChart"
      class="flex flex-grow-1 flex-wrap"
      :class="{ 'opacity-60': tornadoChart?.pending.value }"
    >
      <div :class="[mediaDesktop ? 'col-6' : 'col-12']">
        <PumpTornadoHeadChart
          draw-legend
          is-horizontal
          :min-frequency="minFrequency"
          :max-frequency="maxFrequency"
          :minimum-height-px="chartMinHeight"
          :aspect-ratio="3"
          x-legend-label="In Situ Flow Rate"
          y-legend-label="TDH"
          :pump-tornado-chart-data="chartingData"
        />
      </div>
      <div :class="[mediaDesktop ? 'col-6' : 'col-12']">
        <PumpTornadoBHPChart
          draw-legend
          is-horizontal
          :min-frequency="minFrequency"
          :max-frequency="maxFrequency"
          :minimum-height-px="chartMinHeight"
          :aspect-ratio="3"
          x-legend-label="In Situ Flow Rate"
          y-legend-label="Req BHP"
          :pump-tornado-chart-data="chartingData"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Ref } from "vue";
import { computed, inject, ref } from "vue";
import PumpFrequencyRange from "./PumpFrequencyRange.vue";
import PumpTornadoHeadChart from "./PumpTornadoHeadChart.vue";
import PumpTornadoBHPChart from "./PumpTornadoBHPChart.vue";
import { usePumpChartingData } from "@/libs/charting";
import type { PumpTornadoChartViewData } from "@/libs/charting";
import { useTornadoChartData } from "@/libs/charting/tornado/worker/useTornadoChartData";
import {
  scenarioCalculationsKey,
  scenarioKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { useSettingsStore } from "@/store";

const props = defineProps<{
  chartMinHeight: number;
  useEvaluationOverrides?: boolean;
  startingFrequencyRange?: [number, number];
  hidePumpSelector?: boolean;
}>();
const emit = defineEmits<{
  (e: "frequencyRangeUpdated", newFrequencyRange: [number, number]): void;
}>();
const mediaDesktop = inject<Ref<boolean>>("mediaDesktop");
const _scenario = injectStrict(scenarioKey);
const _sizing = injectStrict(sizingKey, null);
const _calcs = injectStrict(scenarioCalculationsKey);

const {
  getLiquidFlowUnit,
  getHydraulicPowerUnit,
  getLargeLengthUnit,
  getPressureUnit
} = useScenarioUnits(_scenario, _sizing);

// The override keys used here will hopefully go away if we add unit controls
// specifically for the pump table/charts
const targetLiquidFlowUnit = getLiquidFlowUnit(
  "Target Liquid Rate.write.STB/D"
);
const targetPowerUnit = getHydraulicPowerUnit("Shaft Limit.convert.hp");
const targetLargeLengthUnit = getLargeLengthUnit("Required TDH.convert.Feet");
const targetPressureUnit = getPressureUnit(
  "Producing Bottomhole Pressure at Datum [Pwf].write.PSIA"
);

const { flags } = useSettingsStore();
const frequencyRange = ref<[number, number]>([
  flags.value.minimumPumpChartFrequency,
  flags.value.maximumPumpChartFrequency
]);
const minFrequency = computed(() => frequencyRange.value[0]);
const maxFrequency = computed(() => frequencyRange.value[1]);
const frequencyRangeUpdated = (val: [number, number]) => {
  emit("frequencyRangeUpdated", val);
};

const usesCorrectedViscosityRate = computed(
  () => chartingData.value?.context.isRateCorrected
);

// Calculated in web workers so the page stays responsive. Evaluation
// overrides are injected on the main thread, so that case stays synchronous.
const tornadoChart = props.useEvaluationOverrides
  ? undefined
  : useTornadoChartData({
      scenarioCalculations: _calcs,
      minFrequency,
      maxFrequency,
      units: {
        liquidFlow: targetLiquidFlowUnit,
        power: targetPowerUnit,
        largeLength: targetLargeLengthUnit,
        pressure: targetPressureUnit
      }
    });

const chartingData = computed<PumpTornadoChartViewData | undefined>(() => {
  if (tornadoChart) return tornadoChart.data.value;
  return usePumpChartingData(
    minFrequency.value,
    maxFrequency.value,
    _calcs,
    targetLiquidFlowUnit.value,
    targetPowerUnit.value,
    targetLargeLengthUnit.value,
    targetPressureUnit.value,
    props.useEvaluationOverrides
  );
});
</script>

<style lang="scss" scoped>
.top {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0.5em;
}
.two-up {
  display: grid;
  gap: 1em;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
}
.chart-grid {
  padding-bottom: 1em;
  display: grid;
  gap: 1em;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));

  > div {
    box-shadow: var(--shadow-main);
    padding: 1.5em 1.5em 0em 1em;
    background-color: var(--surface-300);
    border-radius: 0.25em;
  }
}
</style>
